import useSWR from 'swr';

import { ILoyalty } from 'containers/Loyalty/api';
import { IDetailResponse, IPaginatedResponse } from 'utils/types';

import { fetcher } from './utils';

export interface ILoyaltyDetail {
  id: number;
  name: string;
  description: string;
  terms_and_conditions: string | null;
  begins_at: string;
  ends_at: string;
  cycle_start_months: string[];
  tiers: {
    id?: number;
    name: string;
    minimum_accumulation_value: any;
  }[];
  images: {
    url: string;
    section: string;
  }[];
  point_transfer_limits?: { limit: number; limit_type: string }[];
  global_limit?: {
    daily_limit?: number;
    weekly_limit?: number;
    monthly_limit?: number;
    yearly_limit?: number;
    balance_limit?: number;
  };
  [key: string]: any;
}

export const useLoyaltyProgram = (id?: number | string | null) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<IDetailResponse<ILoyaltyDetail>>(
    id ? `/v4/dash/loyalty_programs/${id}` : null,
    fetcher
  );

  return { data: data?.data, isLoading, error, mutate, isValidating };
};

export const useLoyaltyProgramList = (params?: object) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<IPaginatedResponse<ILoyalty>>(
    { url: '/v4/dash/loyalty_programs', params },
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

const fetchLoyaltyProgramsByIds = async (ids: Array<number | string>) => {
  const responses = await Promise.all(
    ids.map(
      id => fetcher(`/v4/dash/loyalty_programs/${id}`) as Promise<IDetailResponse<ILoyaltyDetail>>
    )
  );

  return responses.map(response => response.data);
};

export const useLoyaltyProgramsByIds = (ids?: Array<number | string> | null) => {
  const sanitizedIds = (ids || []).filter(id => id !== null && id !== undefined);
  const shouldFetch = sanitizedIds.length > 0;
  const key = shouldFetch ? ['loyaltyProgramsByIds', sanitizedIds.join(',')] : null;
  const { data, error, isLoading, mutate, isValidating } = useSWR<ILoyaltyDetail[]>(key, () =>
    fetchLoyaltyProgramsByIds(sanitizedIds)
  );

  return { data, error, isLoading, mutate, isValidating };
};

export interface ILoyaltyReviewCycle {
  starts_at: string;
  frequency_period: number;
  cycles: number;
  calendar_year_expiry_month: number | null;
}

export interface IExchangeRate {
  id: number;
  destination_amount: number;
  destination_stored_value_campaign_id: number;
  source_amount: number;
  source_stored_value_campaign_id: number;
  state: string;
  pointPerDay?: number | null;
  pointPerMonth?: number | null;
  pointPerYear?: number | null;
}

export const useLoyaltyReviewCycleList = (loyaltyId?: number | string | null) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    IDetailResponse<ILoyaltyReviewCycle[]>
  >(loyaltyId ? `/v4/dash/loyalty_programs/${loyaltyId}/review_cycles` : null, fetcher);

  return { data: data?.data, isLoading, error, mutate, isValidating };
};

export const useExchangeRateList = (params?: Record<string, any>) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    IPaginatedResponse<IExchangeRate>
  >({ url: '/v4/dash/exchange_rates', params }, fetcher);

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};
